// @ts-nocheck
import React from 'react';
import { FONT_STYLES, TEMPLATES } from '../data/initialData';
import './ResumePreview.css';
import './ResumePreviewFontOverrides.css';

/* ── Shared helper components ── */

function ContactItem({ children }) {
  if (!children) return null;
  return <span className="rp-contact-item">{children}</span>;
}

function SectionHeading({ children, accent }) {
  return (
    <div
      className="rp-section-heading"
      style={{
        color: accent,
        borderBottomColor: accent,
        fontFamily: 'var(--rp-heading-font)',
      }}
    >
      {children}
    </div>
  );
}

function BulletList({ text }) {
  if (!text) return null;
  return (
    <ul className="rp-bullets">
      {text.split('\n').filter(l => l.trim()).map((line, i) => (
        <li key={i}>{line.replace(/^[-•*]\s*/, '')}</li>
      ))}
    </ul>
  );
}

function SkillTags({ items, accent }) {
  if (!items) return null;
  return (
    <span className="rp-skill-tags">
      {items.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
        <span key={i} className="rp-skill-tag" style={{
          background: `${accent}20`,
          color: accent,
          border: `1px solid ${accent}99`,
          display: 'inline-block',
          lineHeight: '1.6',
          verticalAlign: 'middle',
          fontFamily: 'var(--rp-body-font)',
        }}>{s}</span>
      ))}
    </span>
  );
}

function SkillsSection({ skills, accent }) {
  if (!skills.languages && !skills.frameworks && !skills.databases && !skills.cloud && !skills.technical && !skills.soft) return null;
  return (
    <section className="rp-section">
      <SectionHeading accent={accent}>Skills</SectionHeading>
      <div className="rp-skills-grid">
        {skills.languages  && <div className="rp-skill-row"><span className="rp-skill-label">Languages:</span><SkillTags items={skills.languages}  accent={accent} /></div>}
        {skills.frameworks && <div className="rp-skill-row"><span className="rp-skill-label">Frameworks:</span><SkillTags items={skills.frameworks} accent={accent} /></div>}
        {skills.databases  && <div className="rp-skill-row"><span className="rp-skill-label">Databases:</span><SkillTags items={skills.databases}  accent={accent} /></div>}
        {skills.cloud      && <div className="rp-skill-row"><span className="rp-skill-label">Cloud & DevOps:</span><SkillTags items={skills.cloud} accent={accent} /></div>}
        {skills.technical  && <div className="rp-skill-row"><span className="rp-skill-label">Other:</span><SkillTags items={skills.technical} accent={accent} /></div>}
        {skills.soft       && <div className="rp-skill-row"><span className="rp-skill-label">Soft Skills:</span><span className="rp-soft-skills">{skills.soft}</span></div>}
      </div>
    </section>
  );
}

function CertsSection({ certifications, accent }) {
  if (!certifications.some(c => c.name)) return null;
  return (
    <section className="rp-section">
      <SectionHeading accent={accent}>Certifications</SectionHeading>
      {certifications.filter(c => c.name).map((c, i) => (
        <div key={i} className="rp-cert-row">
          <span>
            <strong>{c.name}</strong>
            {c.issuer && <span className="rp-cert-issuer"> — {c.issuer}</span>}
            {c.credentialId && <span className="rp-cert-id"> (ID: {c.credentialId})</span>}
          </span>
          {c.date && <span className="rp-entry-date">{c.date}</span>}
        </div>
      ))}
    </section>
  );
}

function CustomSectionsSection({ customSections, accent }) {
  if (!customSections || !customSections.length) return null;
  const filled = customSections.filter(s => s.title && s.items);
  if (!filled.length) return null;
  return (
    <>
      {filled.map((sec, i) => (
        <section key={i} className="rp-section">
          <SectionHeading accent={accent}>{sec.title}</SectionHeading>
          <BulletList text={sec.items} />
        </section>
      ))}
    </>
  );
}

function EducationSection({ education, accent }) {
  if (!education.some(e => e.school)) return null;
  return (
    <section className="rp-section">
      <SectionHeading accent={accent}>Education</SectionHeading>
      {education.filter(e => e.school).map((ed, i) => (
        <div key={i} className="rp-entry">
          <div className="rp-entry-row">
            <strong className="rp-entry-title">{ed.school}</strong>
            <span className="rp-entry-date">{ed.startYear}{ed.current ? ' – Present' : ed.endYear ? ` – ${ed.endYear}` : ''}</span>
          </div>
          <div className="rp-entry-sub">
            {ed.degree}{ed.field ? ` — ${ed.field}` : ''}{ed.gpa ? ` | GPA: ${ed.gpa}` : ''}{ed.percentage ? ` | Percentage: ${ed.percentage.replace(/%$/, '')}%` : ''}{ed.honors ? ` | ${ed.honors}` : ''}
          </div>
          {ed.courses && <div className="rp-courses"><strong>Courses:</strong> {ed.courses}</div>}
        </div>
      ))}
    </section>
  );
}

function ExperienceSection({ experience, accent }) {
  if (!experience.some(e => e.company || e.role)) return null;
  return (
    <section className="rp-section">
      <SectionHeading accent={accent}>Experience</SectionHeading>
      {experience.filter(e => e.company || e.role).map((ex, i) => (
        <div key={i} className="rp-entry">
          <div className="rp-entry-row">
            <strong className="rp-entry-title">{ex.role}</strong>
            <span className="rp-entry-date">{ex.startDate}{ex.current ? ' – Present' : ex.endDate ? ` – ${ex.endDate}` : ''}</span>
          </div>
          <div className="rp-entry-sub">{ex.company}{ex.location ? ` | ${ex.location}` : ''}</div>
          <BulletList text={ex.bullets} />
        </div>
      ))}
    </section>
  );
}

function ProjectsSection({ projects, accent }) {
  if (!projects.some(p => p.name)) return null;
  return (
    <section className="rp-section">
      <SectionHeading accent={accent}>Projects</SectionHeading>
      {projects.filter(p => p.name).map((p, i) => (
        <div key={i} className="rp-entry">
          <div className="rp-entry-row">
            <strong className="rp-entry-title">
              {p.name}
              {p.tech && <span className="rp-project-tech" style={{ color: accent }}> — {p.tech}</span>}
            </strong>
            <span className="rp-entry-date">{[p.github, p.link].filter(Boolean).join(' | ')}</span>
          </div>
          {p.description && <p className="rp-project-desc">{p.description}</p>}
        </div>
      ))}
    </section>
  );
}

/* ── Main export ── */

export default function ResumePreview({ data, templateId, customColor, fontStyleId = 'template' }) {
  const template = TEMPLATES.find(t => t.id === templateId) || TEMPLATES[0];
  const selectedFont = FONT_STYLES.find((font) => font.id === fontStyleId) || FONT_STYLES[0];
  const fontFamily = selectedFont.fontFamily || template.fontFamily;
  const headingFont = selectedFont.headingFont || template.headingFont;
  const { style } = template;
  const accent = customColor || template.accent;
  const { personal, education, experience, projects, skills, certifications, customSections } = data;
  const fontVars = {
    '--rp-body-font': fontFamily,
    '--rp-heading-font': headingFont,
  };

  const contactLine = [
    personal.email,
    personal.phone,
    personal.location,
    personal.linkedin  && `LinkedIn: ${personal.linkedin}`,
    personal.github    && `GitHub: ${personal.github}`,
    personal.portfolio && personal.portfolio,
  ].filter(Boolean);

  /* ════════════════════════════════════
     SIDEBAR — two-column coloured panel
  ════════════════════════════════════ */
  if (style === 'sidebar') {
    return (
      <div id="resume-print-area" className="rp-wrap rp-style-sidebar" style={{ ...fontVars, fontFamily, padding: 0, display: 'flex', minHeight: 900 }}>

        {/* ── Left panel ── */}
        <div className="rp-sidebar-left" style={{ background: accent }}>
          {personal.photo && (
            <img src={personal.photo} alt={personal.name} className="rp-photo rp-sidebar-photo"
              style={{ borderColor: 'rgba(255,255,255,0.5)' }} />
          )}
          <div className="rp-sidebar-name" style={{ fontFamily: headingFont }}>{personal.name || 'Your Name'}</div>

          <div className="rp-sidebar-contacts">
            {personal.email    && <div className="rp-sidebar-contact-item">✉ {personal.email}</div>}
            {personal.phone    && <div className="rp-sidebar-contact-item">📱 {personal.phone}</div>}
            {personal.location && <div className="rp-sidebar-contact-item">📍 {personal.location}</div>}
            {personal.linkedin && <div className="rp-sidebar-contact-item">in {personal.linkedin}</div>}
            {personal.github   && <div className="rp-sidebar-contact-item">⌥ {personal.github}</div>}
            {personal.portfolio && <div className="rp-sidebar-contact-item">🌐 {personal.portfolio}</div>}
          </div>

          {(skills.languages || skills.frameworks || skills.databases || skills.cloud || skills.technical || skills.soft) && (
            <>
              <div className="rp-sidebar-section-title">SKILLS</div>
              {skills.languages  && <><div className="rp-sidebar-skill-label">Languages</div><div className="rp-sidebar-skill-val">{skills.languages}</div></>}
              {skills.frameworks && <><div className="rp-sidebar-skill-label">Frameworks</div><div className="rp-sidebar-skill-val">{skills.frameworks}</div></>}
              {skills.databases  && <><div className="rp-sidebar-skill-label">Databases</div><div className="rp-sidebar-skill-val">{skills.databases}</div></>}
              {skills.cloud      && <><div className="rp-sidebar-skill-label">Cloud & Tools</div><div className="rp-sidebar-skill-val">{skills.cloud}</div></>}
              {skills.technical  && <><div className="rp-sidebar-skill-label">Other</div><div className="rp-sidebar-skill-val">{skills.technical}</div></>}
              {skills.soft       && <><div className="rp-sidebar-skill-label">Soft Skills</div><div className="rp-sidebar-skill-val">{skills.soft}</div></>}
            </>
          )}

          {certifications.some(c => c.name) && (
            <>
              <div className="rp-sidebar-section-title">CERTIFICATIONS</div>
              {certifications.filter(c => c.name).map((c, i) => (
                <div key={i} className="rp-sidebar-cert">
                  <strong>{c.name}</strong>
                  {c.issuer && <div style={{ fontSize: 10, opacity: 0.8 }}>{c.issuer}</div>}
                  {c.date   && <div style={{ fontSize: 10, opacity: 0.7 }}>{c.date}</div>}
                </div>
              ))}
            </>
          )}
        </div>

        {/* ── Right content ── */}
        <div className="rp-sidebar-right">
          {personal.summary && (
            <section className="rp-section">
              <SectionHeading accent={accent}>Summary</SectionHeading>
              <p className="rp-summary">{personal.summary}</p>
            </section>
          )}
          <EducationSection education={education} accent={accent} />
          <ExperienceSection experience={experience} accent={accent} />
          <ProjectsSection projects={projects} accent={accent} />
          <CustomSectionsSection customSections={customSections} accent={accent} />
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════
     BOLD — full-width coloured header
  ════════════════════════════════════ */
  if (style === 'bold') {
    return (
      <div id="resume-print-area" className="rp-wrap rp-style-bold" style={{ ...fontVars, fontFamily, padding: 0 }}>
        <header className="rp-bold-header" style={{ background: accent }}>
          <div className="rp-bold-header-inner">
            {personal.photo && (
              <img src={personal.photo} alt={personal.name} className="rp-photo rp-bold-photo"
                style={{ borderColor: 'rgba(255,255,255,0.6)' }} />
            )}
            <div>
              <div className="rp-bold-name" style={{ fontFamily: headingFont }}>{personal.name || 'Your Name'}</div>
              <div className="rp-bold-contacts">
                {contactLine.map((c, i) => <ContactItem key={i}>{c}</ContactItem>)}
              </div>
            </div>
          </div>
          {personal.summary && <p className="rp-bold-summary">{personal.summary}</p>}
        </header>
        <div className="rp-bold-body">
          <EducationSection education={education} accent={accent} />
          <ExperienceSection experience={experience} accent={accent} />
          <ProjectsSection projects={projects} accent={accent} />
          <SkillsSection skills={skills} accent={accent} />
          <CertsSection certifications={certifications} accent={accent} />
          <CustomSectionsSection customSections={customSections} accent={accent} />
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════
     ELEGANT — centred serif header
  ════════════════════════════════════ */
  if (style === 'elegant') {
    return (
      <div id="resume-print-area" className="rp-wrap rp-style-elegant" style={{ ...fontVars, fontFamily }}>
        <header className="rp-elegant-header">
          {personal.photo && (
            <img src={personal.photo} alt={personal.name} className="rp-photo rp-elegant-photo"
              style={{ borderColor: accent }} />
          )}
          <div className="rp-elegant-name" style={{ color: accent, fontFamily: headingFont }}>
            {personal.name || 'Your Name'}
          </div>
          <div className="rp-contacts rp-elegant-contacts">
            {contactLine.map((c, i) => <ContactItem key={i}>{c}</ContactItem>)}
          </div>
          {personal.summary && (
            <div className="rp-elegant-summary-wrap" style={{ borderColor: accent }}>
              <p className="rp-summary">{personal.summary}</p>
            </div>
          )}
        </header>
        <EducationSection education={education} accent={accent} />
        <ExperienceSection experience={experience} accent={accent} />
        <ProjectsSection projects={projects} accent={accent} />
        <SkillsSection skills={skills} accent={accent} />
        <CertsSection certifications={certifications} accent={accent} />
        <CustomSectionsSection customSections={customSections} accent={accent} />
      </div>
    );
  }

  /* ════════════════════════════════════
     CREATIVE — left accent border
  ════════════════════════════════════ */
  if (style === 'creative') {
    return (
      <div id="resume-print-area" className="rp-wrap rp-style-creative"
        style={{ ...fontVars, fontFamily, borderLeft: `5px solid ${accent}`, paddingLeft: 40 }}>
        <header className="rp-header" style={{ borderBottomColor: accent }}>
          <div className={`rp-header-inner ${personal.photo ? 'rp-header-has-photo' : ''}`}>
            {personal.photo && (
              <img src={personal.photo} alt={personal.name} className="rp-photo" style={{ borderColor: accent }} />
            )}
            <div className="rp-header-text">
              <div className="rp-name" style={{ color: accent, fontFamily: headingFont, fontWeight: 800 }}>
                {personal.name || 'Your Name'}
              </div>
              <div className="rp-contacts">
                {contactLine.map((c, i) => <ContactItem key={i}>{c}</ContactItem>)}
              </div>
              {personal.summary && <p className="rp-summary">{personal.summary}</p>}
            </div>
          </div>
        </header>
        <EducationSection education={education} accent={accent} />
        <ExperienceSection experience={experience} accent={accent} />
        <ProjectsSection projects={projects} accent={accent} />
        <SkillsSection skills={skills} accent={accent} />
        <CertsSection certifications={certifications} accent={accent} />
        <CustomSectionsSection customSections={customSections} accent={accent} />
      </div>
    );
  }

  /* ════════════════════════════════════
     COMPACT — dense information layout
  ════════════════════════════════════ */
  if (style === 'compact') {
    return (
      <div id="resume-print-area" className="rp-wrap rp-style-compact"
        style={{ ...fontVars, fontFamily, padding: '28px 40px', fontSize: 12.5 }}>
        <header style={{ borderBottom: `2.5px solid ${accent}`, paddingBottom: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {personal.photo && (
              <img src={personal.photo} alt={personal.name} className="rp-photo"
                style={{ width: 60, height: 60, borderColor: accent }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: accent, fontFamily: headingFont, textTransform: 'uppercase', letterSpacing: 1 }}>
                {personal.name || 'Your Name'}
              </div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                {[personal.email, personal.phone, personal.location].filter(Boolean).join('  ·  ')}
              </div>
              {(personal.linkedin || personal.github || personal.portfolio) && (
                <div style={{ fontSize: 11, color: '#555' }}>
                  {[personal.linkedin && `LinkedIn: ${personal.linkedin}`, personal.github && `GitHub: ${personal.github}`, personal.portfolio].filter(Boolean).join('  ·  ')}
                </div>
              )}
            </div>
          </div>
          {personal.summary && (
            <p style={{ fontSize: 12, color: '#333', marginTop: 8, lineHeight: 1.5 }}>{personal.summary}</p>
          )}
        </header>

        {education.some(e => e.school) && (
          <section className="rp-section" style={{ marginBottom: 12 }}>
            <SectionHeading accent={accent}>Education</SectionHeading>
            {education.filter(e => e.school).map((ed, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <div className="rp-entry-row">
                  <strong>{ed.school}</strong>
                  <span className="rp-entry-date">{ed.startYear}{ed.current ? ' – Present' : ed.endYear ? ` – ${ed.endYear}` : ''}</span>
                </div>
                <div className="rp-entry-sub" style={{ fontSize: 11.5 }}>
                  {ed.degree}{ed.field ? ` — ${ed.field}` : ''}{ed.gpa ? ` | GPA: ${ed.gpa}` : ''}{ed.percentage ? ` | Percentage: ${ed.percentage.replace(/%$/, '')}%` : ''}{ed.honors ? ` | ${ed.honors}` : ''}
                </div>
                {ed.courses && <div className="rp-courses">{ed.courses}</div>}
              </div>
            ))}
          </section>
        )}

        {experience.some(e => e.company || e.role) && (
          <section className="rp-section" style={{ marginBottom: 12 }}>
            <SectionHeading accent={accent}>Experience</SectionHeading>
            {experience.filter(e => e.company || e.role).map((ex, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div className="rp-entry-row">
                  <strong>{ex.role}</strong>
                  <span className="rp-entry-date">{ex.startDate}{ex.current ? ' – Present' : ex.endDate ? ` – ${ex.endDate}` : ''}</span>
                </div>
                <div className="rp-entry-sub" style={{ fontSize: 11.5 }}>{ex.company}{ex.location ? ` | ${ex.location}` : ''}</div>
                <BulletList text={ex.bullets} />
              </div>
            ))}
          </section>
        )}

        {projects.some(p => p.name) && (
          <section className="rp-section" style={{ marginBottom: 12 }}>
            <SectionHeading accent={accent}>Projects</SectionHeading>
            {projects.filter(p => p.name).map((p, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <div className="rp-entry-row">
                  <strong>{p.name}</strong>
                  <span className="rp-entry-date">{[p.github, p.link].filter(Boolean).join(' | ')}</span>
                </div>
                {p.tech && <div style={{ fontSize: 11, color: accent, marginBottom: 2 }}>{p.tech}</div>}
                {p.description && <p className="rp-project-desc" style={{ fontSize: 11.5 }}>{p.description}</p>}
              </div>
            ))}
          </section>
        )}

        <SkillsSection skills={skills} accent={accent} />
        <CertsSection certifications={certifications} accent={accent} />
        <CustomSectionsSection customSections={customSections} accent={accent} />
      </div>
    );
  }

  /* ════════════════════════════════════
     DEFAULT — classic / modern / minimal / executive / tech
  ════════════════════════════════════ */
  return (
    <div id="resume-print-area" className={`rp-wrap rp-style-${style}`} style={{ ...fontVars, fontFamily }}>

      {/* Header */}
      <header className="rp-header" style={{ borderBottomColor: accent }}>
        <div className={`rp-header-inner ${personal.photo ? 'rp-header-has-photo' : ''}`}>
          {personal.photo && (
            <img src={personal.photo} alt={personal.name || 'Profile'} className="rp-photo"
              style={{ borderColor: accent }} />
          )}
          <div className="rp-header-text">
            <div className="rp-name" style={{ color: accent, fontFamily: headingFont }}>
              {personal.name || 'Your Name'}
            </div>
            <div className="rp-contacts">
              {contactLine.map((c, i) => <ContactItem key={i}>{c}</ContactItem>)}
            </div>
            {personal.summary && <p className="rp-summary">{personal.summary}</p>}
          </div>
        </div>
      </header>

      {/* Education */}
      {education.some(e => e.school) && (
        <section className="rp-section">
          <SectionHeading accent={accent}>Education</SectionHeading>
          {education.filter(e => e.school).map((ed, i) => (
            <div key={i} className="rp-entry">
              <div className="rp-entry-row">
                <strong className="rp-entry-title">{ed.school}</strong>
                <span className="rp-entry-date">{ed.startYear}{ed.current ? ' – Present' : ed.endYear ? ` – ${ed.endYear}` : ''}</span>
              </div>
              <div className="rp-entry-sub">
                {ed.degree}{ed.field ? ` — ${ed.field}` : ''}{ed.gpa ? ` | GPA: ${ed.gpa}` : ''}{ed.percentage ? ` | Percentage: ${ed.percentage.replace(/%$/, '')}%` : ''}{ed.honors ? ` | ${ed.honors}` : ''}
              </div>
              {ed.courses && <div className="rp-courses"><strong>Relevant Courses:</strong> {ed.courses}</div>}
            </div>
          ))}
        </section>
      )}

      {/* Experience */}
      {experience.some(e => e.company || e.role) && (
        <section className="rp-section">
          <SectionHeading accent={accent}>Experience</SectionHeading>
          {experience.filter(e => e.company || e.role).map((ex, i) => (
            <div key={i} className="rp-entry">
              <div className="rp-entry-row">
                <strong className="rp-entry-title">{ex.role}</strong>
                <span className="rp-entry-date">{ex.startDate}{ex.current ? ' – Present' : ex.endDate ? ` – ${ex.endDate}` : ''}</span>
              </div>
              <div className="rp-entry-sub">{ex.company}{ex.location ? ` | ${ex.location}` : ''}</div>
              <BulletList text={ex.bullets} />
            </div>
          ))}
        </section>
      )}

      {/* Projects */}
      {projects.some(p => p.name) && (
        <section className="rp-section">
          <SectionHeading accent={accent}>Projects</SectionHeading>
          {projects.filter(p => p.name).map((p, i) => (
            <div key={i} className="rp-entry">
              <div className="rp-entry-row">
                <strong className="rp-entry-title">
                  {p.name}
                  {p.tech && <span className="rp-project-tech" style={{ color: accent }}> — {p.tech}</span>}
                </strong>
                <span className="rp-entry-date">{[p.github, p.link].filter(Boolean).join(' | ')}</span>
              </div>
              {p.description && <p className="rp-project-desc">{p.description}</p>}
            </div>
          ))}
        </section>
      )}

      {/* Skills */}
      {(skills.languages || skills.frameworks || skills.databases || skills.cloud || skills.technical || skills.soft) && (
        <section className="rp-section">
          <SectionHeading accent={accent}>Skills</SectionHeading>
          <div className="rp-skills-grid">
            {skills.languages  && <div className="rp-skill-row"><span className="rp-skill-label">Languages:</span><SkillTags items={skills.languages}  accent={accent} /></div>}
            {skills.frameworks && <div className="rp-skill-row"><span className="rp-skill-label">Frameworks:</span><SkillTags items={skills.frameworks} accent={accent} /></div>}
            {skills.databases  && <div className="rp-skill-row"><span className="rp-skill-label">Databases:</span><SkillTags items={skills.databases}  accent={accent} /></div>}
            {skills.cloud      && <div className="rp-skill-row"><span className="rp-skill-label">Cloud & DevOps:</span><SkillTags items={skills.cloud} accent={accent} /></div>}
            {skills.technical  && <div className="rp-skill-row"><span className="rp-skill-label">Other:</span><SkillTags items={skills.technical}  accent={accent} /></div>}
            {skills.soft       && <div className="rp-skill-row"><span className="rp-skill-label">Soft Skills:</span><span className="rp-soft-skills">{skills.soft}</span></div>}
          </div>
        </section>
      )}

      {/* Certifications */}
      {certifications.some(c => c.name) && (
        <section className="rp-section">
          <SectionHeading accent={accent}>Certifications</SectionHeading>
          {certifications.filter(c => c.name).map((c, i) => (
            <div key={i} className="rp-cert-row">
              <span>
                <strong>{c.name}</strong>
                {c.issuer       && <span className="rp-cert-issuer"> — {c.issuer}</span>}
                {c.credentialId && <span className="rp-cert-id"> (ID: {c.credentialId})</span>}
              </span>
              {c.date && <span className="rp-entry-date">{c.date}</span>}
            </div>
          ))}
        </section>
      )}

      {/* Custom Sections */}
      <CustomSectionsSection customSections={customSections} accent={accent} />
    </div>
  );
}
